/**
 * SEDC BMS Simulation Service
 * ============================
 * Simulates real-time values for Seoul Energy Dream Center BMS points.
 * Point IDs and Modbus registers are sourced from "BMS Point.xlsx".
 *
 * Architecture: replace getSimulatedValue() calls with actual Modbus/BACnet
 * API requests using the modbus_reg addresses when live data is available.
 */

export type SystemMode = 'COOLING' | 'HEATING' | 'OFF';

// ── Helpers ───────────────────────────────────────────────────────────────────

function noise(amplitude: number): number {
  return (Math.random() - 0.5) * 2 * amplitude;
}

function sinWave(period_ms: number, amplitude: number, phase_rad = 0): number {
  return Math.sin(Date.now() / period_ms * Math.PI * 2 + phase_rad) * amplitude;
}

/**
 * Outdoor temperature — Seoul cooling season (Jun–Aug) profile.
 * Base: 30°C (Seoul July avg), daily swing ±5°C, peaks at 14:00.
 * Uses virtual cooling-season time so values match EnergyPlus RunPeriod
 * even when the real calendar date is outside Jun–Aug.
 */
function outdoorTemp(): number {
  const hourFrac = (new Date().getHours() + new Date().getMinutes() / 60) / 24;
  // Peak ~14:00 → phase offset -(14/24)*2π
  return 30 + Math.sin((hourFrac - 0.375) * Math.PI * 2) * 5 + noise(0.5);
}

// ── Core value generator ──────────────────────────────────────────────────────

function generateValue(point_id: string, mode: SystemMode): number | null {
  const oat = outdoorTemp();
  const isCooling = mode === 'COOLING';
  const isHeating = mode === 'HEATING';
  // 냉방철 기준: 냉방 중 26°C, 냉방 없을 시 30°C 수준
  const zone = (isCooling ? 26 : 30) + sinWave(900_000, 1.0) + noise(0.3);

  // ------------------------------------------------------------------
  // AHU (공조기) — Modbus: 35078~35310
  // ------------------------------------------------------------------
  if (point_id === 'AHU.D4000') return Math.round(55 + sinWave(1_200_000, 6) + noise(1.5));   // RA 습도 %
  if (point_id === 'AHU.D4001') return +(zone + noise(0.2)).toFixed(1);                        // RA 온도 °C
  if (point_id === 'AHU.D4002') return +(isCooling ? 15.5 + noise(0.4) : 22 + noise(0.4)).toFixed(1); // SA 온도 °C
  if (point_id === 'AHU.D4003') return +oat.toFixed(1);                                         // OA 온도 °C
  if (point_id === 'AHU.D4012') return Math.round(8_500 + noise(600));                          // SA 풍량 CMH
  if (point_id === 'AHU.D4013') return Math.round(8_000 + noise(500));                          // RA 풍량 CMH
  if (point_id === 'AHU.D4014') return Math.round(185 + noise(20));                             // SA 덕트 차압 Pa
  if (point_id === 'AHU.D4015') return Math.round(155 + noise(15));                             // RA 덕트 차압 Pa

  // ------------------------------------------------------------------
  // G_D — 지열히트펌프 (HP1/HP2/HP3) + 외기 + CO2 센서
  // ------------------------------------------------------------------
  if (point_id === 'DDC_G.D.D0100') return +oat.toFixed(1);                                     // 외기온도 °C
  if (point_id === 'DDC_G.D.D0151') return Math.round(62 + sinWave(1_800_000, 8) + noise(2));  // 외기습도 %

  // HP1 (DDC_G.D.D0101~D0105)
  if (point_id === 'DDC_G.D.D0101') return +(isCooling ? 17 + noise(0.3) : 9 + noise(0.3)).toFixed(1);  // HP1 1차배관(S)
  if (point_id === 'DDC_G.D.D0102') return +(isCooling ? 13 + noise(0.3) : 5 + noise(0.3)).toFixed(1);  // HP1 1차배관(R)
  if (point_id === 'DDC_G.D.D0103') return +(isCooling ? 12.2 + noise(0.3) : 40.5 + noise(0.5)).toFixed(1); // HP1 2차배관(R) ← return from building
  if (point_id === 'DDC_G.D.D0104') return +(isCooling ? 7.2 + noise(0.2) : 45.2 + noise(0.4)).toFixed(1);  // HP1 2차배관(S) → supply to building

  // HP2 (DDC_G.D.D0110~D0115)
  if (point_id === 'DDC_G.D.D0110') return +(isCooling ? 17.5 + noise(0.3) : 9.5 + noise(0.3)).toFixed(1);
  if (point_id === 'DDC_G.D.D0111') return +(isCooling ? 13.5 + noise(0.3) : 5.5 + noise(0.3)).toFixed(1);
  if (point_id === 'DDC_G.D.D0112') return +(isCooling ? 7.1 + noise(0.2) : 44.8 + noise(0.4)).toFixed(1);  // HP2 2차배관(S)
  if (point_id === 'DDC_G.D.D0113') return +(isCooling ? 11.8 + noise(0.3) : 40.2 + noise(0.5)).toFixed(1); // HP2 2차배관(R)

  // HP3 (DDC_G.D.D0119~D0124)
  if (point_id === 'DDC_G.D.D0119') return +(isCooling ? 18 + noise(0.3) : 10 + noise(0.3)).toFixed(1);
  if (point_id === 'DDC_G.D.D0120') return +(isCooling ? 14 + noise(0.3) : 6 + noise(0.3)).toFixed(1);
  if (point_id === 'DDC_G.D.D0121') return +(isCooling ? 7.3 + noise(0.2) : 45.2 + noise(0.4)).toFixed(1);  // HP3 2차배관(S)
  if (point_id === 'DDC_G.D.D0122') return +(isCooling ? 12.1 + noise(0.3) : 40.8 + noise(0.5)).toFixed(1); // HP3 2차배관(R)

  // Buffer Tank (G_D side — 버퍼탱크1)
  if (point_id === 'DDC_G.D.D0107') return +(isCooling ? 8.5 + noise(0.2) : 43.0 + noise(0.3)).toFixed(1); // 상
  if (point_id === 'DDC_G.D.D0108') return +(isCooling ? 9.2 + noise(0.2) : 41.0 + noise(0.3)).toFixed(1); // 중
  if (point_id === 'DDC_G.D.D0109') return +(isCooling ? 10.0 + noise(0.2) : 39.0 + noise(0.3)).toFixed(1); // 하

  // Buffer Tank 2 (G_D — second geothermal branch)
  if (point_id === 'DDC_G.D.D0116') return +(isCooling ? 8.4 + noise(0.2) : 42.8 + noise(0.3)).toFixed(1); // 상
  if (point_id === 'DDC_G.D.D0117') return +(isCooling ? 9.1 + noise(0.2) : 40.8 + noise(0.3)).toFixed(1); // 중
  if (point_id === 'DDC_G.D.D0118') return +(isCooling ? 9.9 + noise(0.2) : 38.8 + noise(0.3)).toFixed(1); // 하

  // Heating Buffer Tank (G_D — 난방버퍼탱크3)
  if (point_id === 'DDC_G.D.D0125') return +(42 + noise(0.5)).toFixed(1); // 상
  if (point_id === 'DDC_G.D.D0126') return +(40 + noise(0.5)).toFixed(1); // 중
  if (point_id === 'DDC_G.D.D0127') return +(38 + noise(0.5)).toFixed(1); // 하

  // CO2 Sensors by zone — ppm
  if (point_id === 'DDC_G.D.D0152') return Math.round(455 + sinWave(1_800_000, 80, 0.0) + noise(20));  // 1F 출입로비
  if (point_id === 'DDC_G.D.D0153') return Math.round(490 + sinWave(1_800_000, 90, 0.5) + noise(25));  // 1F 전시홍보1
  if (point_id === 'DDC_G.D.D0154') return Math.round(470 + sinWave(1_800_000, 70, 1.0) + noise(20));  // 1F 전시홍보2
  if (point_id === 'DDC_G.D.D0155') return Math.round(480 + sinWave(1_800_000, 85, 1.5) + noise(20));  // 1F 전시홍보3
  if (point_id === 'DDC_G.D.D0156') return Math.round(525 + sinWave(1_800_000, 60, 2.0) + noise(15));  // 1F 화장실
  if (point_id === 'DDC_G.D.D0158') return Math.round(445 + sinWave(1_800_000, 75, 0.3) + noise(20));  // 2F 전시홍보1
  if (point_id === 'DDC_G.D.D0159') return Math.round(460 + sinWave(1_800_000, 80, 0.8) + noise(20));  // 2F 전시홍보2
  if (point_id === 'DDC_G.D.D0160') return Math.round(435 + sinWave(1_800_000, 70, 1.3) + noise(15));  // 2F 전시홍보3
  if (point_id === 'DDC_G.D.D0161') return Math.round(495 + sinWave(1_800_000, 50, 1.8) + noise(15));  // 2F 전시창고
  if (point_id === 'DDC_G.D.D0162') return Math.round(515 + sinWave(1_800_000, 55, 2.3) + noise(15));  // 2F 화장실

  // Airflow sensors
  if (point_id === 'DDC_G.D.D0174') return Math.round(350 + noise(30));  // 1층 화장실 배기 CMH
  if (point_id === 'DDC_G.D.D0175') return Math.round(320 + noise(25));  // 2층 화장실 배기 CMH

  // ------------------------------------------------------------------
  // M_D — 전기식 냉동기 (Electric Chiller) + 배관 + 구역 온도
  // ------------------------------------------------------------------

  // 냉동기 배관
  if (point_id === 'DDC_M.D.D0100') return +(isCooling ? 13.0 + noise(0.4) : 35 + noise(0.5)).toFixed(1); // 냉동기 1차배관(R)
  if (point_id === 'DDC_M.D.D0101') return +(isCooling ? 8.5 + noise(0.3) : 42 + noise(0.5)).toFixed(1);  // 냉동기 1차배관(S)
  if (point_id === 'DDC_M.D.D0102') return isCooling ? +(12.5 + noise(0.3)).toFixed(1) : null; // 현장 교정: 냉동기 IN(환수)
  if (point_id === 'DDC_M.D.D0103') return isCooling ? +(7.8 + noise(0.2)).toFixed(1) : null; // 현장 교정: 냉동기 OUT(공급)

  // Buffer Tank 4 — 냉방(1,2,3층)용 = TK-4 (feeds TABS + Convector)
  if (point_id === 'DDC_M.D.D0104') return +(isCooling ? 7.4 + noise(0.2) : 45 + noise(0.3)).toFixed(1); // 상
  if (point_id === 'DDC_M.D.D0105') return +(isCooling ? 8.2 + noise(0.2) : 43 + noise(0.3)).toFixed(1); // 중
  if (point_id === 'DDC_M.D.D0106') return +(isCooling ? 9.1 + noise(0.2) : 41 + noise(0.3)).toFixed(1); // 하

  // Buffer Tank 5 — 공조용 = TK-5 (feeds AHU only)
  if (point_id === 'DDC_M.D.D0107') return isCooling ? +(7.2 + noise(0.2)).toFixed(1) : null; // 상
  if (point_id === 'DDC_M.D.D0108') return isCooling ? +(8.0 + noise(0.2)).toFixed(1) : null; // 중
  if (point_id === 'DDC_M.D.D0109') return isCooling ? +(8.8 + noise(0.2)).toFixed(1) : null; // 하

  // AHU 배관 (냉방/난방)
  if (point_id === 'DDC_M.D.D0110') return isCooling ? +(7.5 + noise(0.2)).toFixed(1) : null;  // 공조기 냉방 배관(S)
  if (point_id === 'DDC_M.D.D0111') return isCooling ? +(12.8 + noise(0.3)).toFixed(1) : null; // 공조기 냉방 배관(R)
  if (point_id === 'DDC_M.D.D0112') return isHeating ? +(42 + noise(0.5)).toFixed(1) : null;   // 공조기 난방 배관(S)
  if (point_id === 'DDC_M.D.D0113') return isHeating ? +(37 + noise(0.5)).toFixed(1) : null;   // 공조기 난방 배관(R)

  // 컨벡터 배관 (3층)
  if (point_id === 'DDC_M.D.D0114') return +(isCooling ? 7.4 + noise(0.2) : 44 + noise(0.4)).toFixed(1); // S
  if (point_id === 'DDC_M.D.D0115') return +(isCooling ? 10.2 + noise(0.3) : 40 + noise(0.4)).toFixed(1); // R

  // 구역 온도 (Zone temperatures) — 냉방철 기준 (냉방 중 25~27°C 범위)
  if (point_id === 'DDC_M.D.D0116') return +(25.5 + sinWave(900_000, 1.0, 0.0) + noise(0.3)).toFixed(1); // 다목적실1
  if (point_id === 'DDC_M.D.D0117') return +(26.0 + sinWave(900_000, 1.2, 0.4) + noise(0.3)).toFixed(1); // 다목적실2
  if (point_id === 'DDC_M.D.D0118') return +(25.8 + sinWave(900_000, 0.8, 0.8) + noise(0.2)).toFixed(1); // 사무실
  if (point_id === 'DDC_M.D.D0119') return +(26.2 + sinWave(900_000, 0.9, 1.2) + noise(0.2)).toFixed(1); // 회의실
  if (point_id === 'DDC_M.D.D0120') return +(26.5 + sinWave(900_000, 1.1, 1.6) + noise(0.3)).toFixed(1); // 체험실
  if (point_id === 'DDC_M.D.D0121') return +(26.0 + sinWave(900_000, 0.8, 2.0) + noise(0.2)).toFixed(1); // 체험준비실&수유실
  if (point_id === 'DDC_M.D.D0122') return +(25.2 + sinWave(900_000, 1.0, 2.4) + noise(0.3)).toFixed(1); // 북카페
  if (point_id === 'DDC_M.D.D0123') return +(26.8 + sinWave(900_000, 1.5, 2.8) + noise(0.4)).toFixed(1); // 로비

  // TABS / 복사냉난방 배관 (1,2층)
  if (point_id === 'DDC_M.D.D0128') return +(isCooling ? 7.4 + noise(0.2) : 43 + noise(0.4)).toFixed(1); // S
  if (point_id === 'DDC_M.D.D0129') return +(isCooling ? 9.1 + noise(0.2) : 39 + noise(0.4)).toFixed(1); // R

  // ------------------------------------------------------------------
  // POWER — 전력량계 (POWER.WHM.*) — kW 순시값으로 시뮬레이션
  // Historical 재생 시 HistoricalService가 실측값으로 자동 대체됨
  // ------------------------------------------------------------------
  // HP1/HP2/HP3 지열히트펌프 합계 전력 (냉방COP≈3.5, 냉방용량 22kW/대)
  if (point_id === 'POWER.WHM.P_GE')  return +(isCooling ? 19.0 + noise(1.5) : 8.0 + noise(0.8)).toFixed(1);
  // AHU 팬 전력
  if (point_id === 'POWER.WHM.P_AHU') return +(11.0 + noise(0.8)).toFixed(1);
  // 전기식 냉동기 전력 — 냉방 운전 중 압축기 소비전력을 포함
  if (point_id === 'POWER.WHM.P_CH')  return +(isCooling ? 18.0 + noise(1.2) : 0.5 + noise(0.1)).toFixed(1);
  // 조명
  if (point_id === 'POWER.WHM.LM_1A') return +(8.0 + noise(0.5)).toFixed(1);
  if (point_id === 'POWER.WHM.LM_1B') return +(5.5 + noise(0.4)).toFixed(1);
  if (point_id === 'POWER.WHM.LM_2A') return +(7.0 + noise(0.5)).toFixed(1);
  if (point_id === 'POWER.WHM.LM_2B') return +(6.5 + noise(0.4)).toFixed(1);
  if (point_id === 'POWER.WHM.LM_3A') return +(9.0 + noise(0.6)).toFixed(1);
  if (point_id === 'POWER.WHM.LM_3B') return +(8.0 + noise(0.5)).toFixed(1);

  return null;
}

// ── Service Class ─────────────────────────────────────────────────────────────

// All point IDs simulated by this service (keyed for fast lookup)
const SIMULATED_POINTS = [
  // POWER — 전력량계
  'POWER.WHM.P_GE', 'POWER.WHM.P_AHU', 'POWER.WHM.P_CH',
  'POWER.WHM.LM_1A', 'POWER.WHM.LM_1B',
  'POWER.WHM.LM_2A', 'POWER.WHM.LM_2B',
  'POWER.WHM.LM_3A', 'POWER.WHM.LM_3B',
  // AHU
  'AHU.D4000', 'AHU.D4001', 'AHU.D4002', 'AHU.D4003',
  'AHU.D4012', 'AHU.D4013', 'AHU.D4014', 'AHU.D4015',
  // G_D — outdoor
  'DDC_G.D.D0100', 'DDC_G.D.D0151',
  // G_D — HP1
  'DDC_G.D.D0101', 'DDC_G.D.D0102', 'DDC_G.D.D0103', 'DDC_G.D.D0104',
  // G_D — HP2
  'DDC_G.D.D0110', 'DDC_G.D.D0111', 'DDC_G.D.D0112', 'DDC_G.D.D0113',
  // G_D — HP3
  'DDC_G.D.D0119', 'DDC_G.D.D0120', 'DDC_G.D.D0121', 'DDC_G.D.D0122',
  // G_D — tanks
  'DDC_G.D.D0107', 'DDC_G.D.D0108', 'DDC_G.D.D0109',
  'DDC_G.D.D0125', 'DDC_G.D.D0126', 'DDC_G.D.D0127',
  // G_D — CO2
  'DDC_G.D.D0152', 'DDC_G.D.D0153', 'DDC_G.D.D0154', 'DDC_G.D.D0155',
  'DDC_G.D.D0156', 'DDC_G.D.D0158', 'DDC_G.D.D0159', 'DDC_G.D.D0160',
  'DDC_G.D.D0161', 'DDC_G.D.D0162', 'DDC_G.D.D0174', 'DDC_G.D.D0175',
  // M_D — chiller
  'DDC_M.D.D0100', 'DDC_M.D.D0101', 'DDC_M.D.D0102', 'DDC_M.D.D0103',
  // M_D — TK4 (1,2,3층 냉방)
  'DDC_M.D.D0104', 'DDC_M.D.D0105', 'DDC_M.D.D0106',
  // M_D — TK5 (공조용)
  'DDC_M.D.D0107', 'DDC_M.D.D0108', 'DDC_M.D.D0109',
  // M_D — AHU piping
  'DDC_M.D.D0110', 'DDC_M.D.D0111', 'DDC_M.D.D0112', 'DDC_M.D.D0113',
  // M_D — convector
  'DDC_M.D.D0114', 'DDC_M.D.D0115',
  // M_D — zone temps
  'DDC_M.D.D0116', 'DDC_M.D.D0117', 'DDC_M.D.D0118', 'DDC_M.D.D0119',
  'DDC_M.D.D0120', 'DDC_M.D.D0121', 'DDC_M.D.D0122', 'DDC_M.D.D0123',
  // M_D — TABS
  'DDC_M.D.D0128', 'DDC_M.D.D0129',
];

import { HistoricalService } from './HistoricalService';

// HistoricalService가 활성 상태면 실측값을 우선 반환한다.
function getHistoricalValue(point_id: string): number | null {
  return HistoricalService.get(point_id);
}

export class BMSSimulationService {
  private static _values: Record<string, number | null> = {};
  private static _overrides: Record<string, number> = {};
  private static _mode: SystemMode = 'COOLING';
  private static _intervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * 외부 데이터(EnergyPlus Tracking Twin 등)로 특정 포인트 값을 덮어씁니다.
   * Historical 모드보다 낮은 우선순위, 시뮬레이션보다 높은 우선순위.
   */
  static injectOverrides(overrides: Record<string, number>) {
    Object.assign(this._overrides, overrides);
  }

  static clearOverrides() {
    this._overrides = {};
  }

  /** Start automatic update loop */
  static start(intervalMs = 2000) {
    if (this._intervalId) return;
    this._update();
    this._intervalId = setInterval(() => this._update(), intervalMs);
  }

  static stop() {
    if (this._intervalId) { clearInterval(this._intervalId); this._intervalId = null; }
  }

  static setMode(mode: SystemMode) {
    this._mode = mode;
    this._update(); // Refresh values immediately on mode change
  }

  private static _update() {
    SIMULATED_POINTS.forEach(id => {
      this._values[id] = generateValue(id, this._mode);
    });
  }

  /**
   * Get value for a point.
   * Historical 모드 활성 시 실측값 우선, 없으면 시뮬레이션 fallback.
   */
  static get(point_id: string, fallback: number = 0): number {
    const hist = getHistoricalValue(point_id);
    if (hist !== null) return hist;
    if (this._overrides[point_id] !== undefined) return this._overrides[point_id];
    const v = this._values[point_id];
    return (v != null) ? v : fallback;
  }

  /** Raw accessor — Historical 모드 우선, 없으면 override, 없으면 시뮬레이션, 없으면 null */
  static getRaw(point_id: string): number | null {
    const hist = getHistoricalValue(point_id);
    if (hist !== null) return hist;
    if (this._overrides[point_id] !== undefined) return this._overrides[point_id];
    return this._values[point_id] ?? null;
  }

  /** Get current system mode */
  static getMode(): SystemMode { return this._mode; }

  /** Get all current values snapshot */
  static getSnapshot(): Record<string, number | null> {
    return { ...this._values };
  }
}
